import './newsletterModal.scss';

import { Storage } from '@capacitor/storage';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  IonButton,
  IonCheckbox,
  IonCol,
  IonGrid,
  IonItem,
  IonRow,
} from '@ionic/react';
import { ErrorMessage, Field, FormikProvider, useFormik } from 'formik';
import { useSubscribeToNewsletter } from 'infrastructure/services/CustomerioGTMTrackingService';
import React, { useEffect } from 'react';
import { Trans } from 'react-i18next';
import CustomModal from 'ui/elements/CustomModal/CustomModal';
import { useContextTranslation } from 'ui/translation';
import AutocompleteForm from 'ui/utils/AutocompleteForm';
import IonicField from 'ui/utils/IonicField';
import * as Yup from 'yup';

async function shouldShowNewsletterModal(setOpened: (opened: boolean) => void) {
  const keys = await Storage.keys();

  if (keys.keys.includes('newsletterDismissed')) {
    return;
  }

  setOpened(true);
}

async function dismissModal(setOpened: (opened: boolean) => void) {
  await Storage.set({ key: 'newsletterDismissed', value: 'true' });
  setOpened(false);
}

export default function NewsletterModal(): JSX.Element {
  const t = useContextTranslation('page.newsletter');

  const [shouldShowModal, setShouldShowModal] = React.useState(false);

  useEffect(() => {
    shouldShowNewsletterModal(setShouldShowModal);
  }, []);

  const subscribeToNewsletter = useSubscribeToNewsletter();

  const formik = useFormik({
    initialValues: {
      email: '',
      name: '',
      postalCode: '',
      marketingConsents: false,
    },
    onSubmit: async values => {
      await subscribeToNewsletter(
        values.email,
        values.name,
        values.postalCode,
        true,
      );
      await dismissModal(setShouldShowModal);
    },
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email(t('validation.email.invalid'))
        .required(t('validation.email.required')),
      name: Yup.string().required(t('validation.name_required')),
      postalCode: Yup.string()
        .matches(/\d{5}/, t('validation.postal_code.invalid'))
        .required(t('validation.postal_code.required')),
      marketingConsents: Yup.boolean()
        .isTrue(t('validation.marketing_consents'))
        .required(t('validation.marketing_consents')),
    }),
    validateOnBlur: false,
    validateOnChange: false,
  });

  const [marketingConsents, setMarketingConsents] = React.useState<boolean>(
    false,
  );

  return (
    <CustomModal
      isOpen={shouldShowModal}
      onDidDismiss={() => dismissModal(setShouldShowModal)}
      cssClass="newsletter-modal"
      overlayCssClass="newsletter-overlay"
    >
      <div className="newsletter-header">
        <span className="newsletter-badge">
          <h3>15€*</h3>
          {t('badge_off')}
        </span>
        <button
          type="button"
          aria-label={t('close')}
          className="close-button"
          onClick={() => dismissModal(setShouldShowModal)}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>
      <IonGrid>
        <h4 className="newsletter-heading">{t('title')}</h4>
        <FormikProvider value={formik}>
          <AutocompleteForm>
            <IonRow>
              <IonCol size="12">
                <Field
                  component={IonicField}
                  name="email"
                  type="email"
                  inputMode="email"
                  autocomplete="email"
                  required
                  placeholder={t('email_placeholder')}
                  showErrors
                />
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol size="12">
                <Field
                  component={IonicField}
                  name="name"
                  autocomplete="name"
                  required
                  placeholder={t('name_placeholder')}
                  showErrors
                />
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol size="12">
                <Field
                  component={IonicField}
                  name="postalCode"
                  autocomplete="postal-code"
                  required
                  placeholder={t('postal_code_placeholder')}
                  showErrors
                />
              </IonCol>
              <IonCol>
                <IonItem className="toc-confirmation" lines="none">
                  <IonCheckbox
                    slot="start"
                    mode="ios"
                    color="secondary"
                    checked={marketingConsents}
                    onIonChange={e => {
                      formik.setFieldValue(
                        'marketingConsents',
                        e.detail.checked,
                      );
                      setMarketingConsents(e.detail.checked);
                    }}
                  />
                  <span className="toc-label">
                    <Trans t={t} i18nKey="marketing_emails">
                      marketing_emails
                      <a
                        className="ion-link"
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://booking.soulhouse.me/dataprotection"
                      >
                        data protection policy
                      </a>
                      <a href="mailto:unsubscribe@soulhouse.me">
                        unsubscribe@soulhouse.me
                      </a>
                      end
                    </Trans>
                  </span>
                </IonItem>
                <ErrorMessage
                  name="marketingConsents"
                  component="div"
                  className="form-error"
                />
              </IonCol>
              <IonCol size="12" className="newsletter-btn">
                <IonButton type="submit" expand="block">
                  {t('subscribe')}
                </IonButton>
              </IonCol>
            </IonRow>
          </AutocompleteForm>
        </FormikProvider>
      </IonGrid>
    </CustomModal>
  );
}
